import { ACTION_LABELS, addDays, type AuditEntryDto, BILLING_REASON_REQUIRED, billingReasonRequired, TICKET_BILLING, TICKET_BILLING_STATES, type TicketBilling, CLAIM_NUMBER_REQUIRED, CLAIM_REMINDER_DAYS, CLAIM_STATES, claimNumberMissing, type FieldProtocol, type InstallationDto, lateInspectionText, type MeDto, type Page, PROTOCOL_DELIVERY_STATES, PROTOCOL_KINDS, type ServiceTicketDto, TASK_STATUSES, type TaskDto, type TaskStatus, TICKET_PRIORITIES, TICKET_STATUSES, TICKET_TYPES, type TicketStatus, warrantyText } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Group, Menu, Modal, MultiSelect, Paper, Select, Stack, Table, Text, Textarea, TextInput } from '@mantine/core';
import { IconAlertTriangle, IconCalendarEvent, IconCheck, IconDots } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { CompanyBadgeById } from '../../components/Badges';
import { QueryState } from '../../components/QueryState';
import { useCompanies } from '../../data';
import { formatDateTime, plural } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { termText } from '../planning/common';
import { PlanDialog } from '../planning/PlanDialog';
import { deliveryColor } from '../planning/ProtocolsBlock';
import { dateOnly, TICKET_COLORS, todayIso } from './common';
import { minHint } from '../../minHint';
import { Changes } from '../AuditPage';
import { ServiceChargeBlock } from './ServiceChargeBlock';

const STEPS: TicketStatus[] = ['nowe', 'do_umowienia', 'zaplanowane', 'w_realizacji', 'wykonane', 'zamkniete'];
const OPEN_TASK = ['do_zaplanowania', 'zaplanowane', 'w_toku', 'wstrzymane'];
/** PDF protokołu jeszcze nie gotowy — ponowna wysyłka niemożliwa */
const PDF_PENDING: string[] = ['czeka_na_synchronizacje', 'oczekuje_na_zdjecia', 'oczekuje_na_pdf', 'blad_pdf'];

/** Przebieg zgłoszenia — gdzie jesteśmy i co dalej. */
function Steps({ status }: { status: TicketStatus }) {
  if (status === 'anulowane')
    return (
      <Badge color="gray" size="lg">
        Anulowane
      </Badge>
    );
  const at = STEPS.indexOf(status);
  return (
    <Group gap={4} wrap="nowrap">
      {STEPS.map((s, i) => (
        <Group key={s} gap={4} wrap="nowrap">
          <Badge size="sm" variant={i === at ? 'filled' : i < at ? 'light' : 'outline'} color={i <= at ? TICKET_COLORS[status] : 'gray'} leftSection={i < at ? <IconCheck size={10} /> : undefined}>
            {TICKET_STATUSES[s]}
          </Badge>
          {i < STEPS.length - 1 && (
            <Text size="xs" c="dimmed">
              ›
            </Text>
          )}
        </Group>
      ))}
    </Group>
  );
}

/**
 * Zgłoszenie serwisowe: na górze przebieg i jedna główna akcja (zaplanuj wizytę / zmień termin / zamknij),
 * pod nią wizyta, dane zgłoszenia, protokoły. Rzadsze akcje w menu „Więcej”.
 */
export function TicketPanel({ id, me }: { id: string; me: MeDto }) {
  const qc = useQueryClient();
  const companies = useCompanies();
  const t = useQuery({ queryKey: ['service-ticket', id], queryFn: () => api<ServiceTicketDto>(`/service-tickets/${id}`) });
  const d = t.data;
  const visit = d?.tasks.find((x) => OPEN_TASK.includes(x.status));
  const visitTask = useQuery({ queryKey: ['task', visit?.id], queryFn: () => api<TaskDto>(`/tasks/${visit!.id}`), enabled: !!visit });
  const [planTask, setPlanTask] = useState<TaskDto | null>(null);
  const [edit, setEdit] = useState<{ priority: string; description: string; warranty: boolean; billing: TicketBilling | null; billingReason: string; companyId: string | null; claimNumber: string; claimState: string; claimDueOn?: string | null; deviceIds: string[] } | null>(null);
  // D15: urządzenia instalacji do zmiany w „Edytuj zgłoszenie”
  const inst = useQuery({ queryKey: ['installation', d?.installationId], queryFn: () => api<InstallationDto>(`/installations/${d!.installationId}`), enabled: !!edit && !!d?.installationId });
  const [action, setAction] = useState<{ action: 'wykonane' | 'zamknij' | 'anuluj'; note: string; date: string; claimNumber?: string; claimState?: string; claimDueOn?: string | null; billing?: TicketBilling | null; billingReason?: string } | null>(null);
  // QA qa10311123 3.3: rozliczenie naprawy po zamknięciu i numer faktury za płatny serwis
  const [billingEdit, setBillingEdit] = useState<{ billing: TicketBilling; reason: string } | null>(null);
  const [invoice, setInvoice] = useState<{ invoiceNumber: string } | null>(null);
  const saveBilling = useMutation({ mutationFn: () => api(`/service-tickets/${id}/billing`, { method: 'PUT', body: billingEdit }), onSuccess: () => (notifyOk('Zapisano rozliczenie'), setBillingEdit(null), refresh()), onError: notifyError });
  const saveInvoice = useMutation({ mutationFn: () => api(`/service-tickets/${id}/invoiced`, { method: 'POST', body: invoice }), onSuccess: () => (notifyOk('Zapisano numer faktury'), setInvoice(null), refresh()), onError: notifyError });
  // QA6 P29: reklamacja u producenta po zamknięciu zgłoszenia
  const [claim, setClaim] = useState<{ claimNumber: string; claimState: string; claimDueOn?: string | null } | null>(null);
  // QA qa10302243 P6: niedoręczone protokoły wręczone na miejscu / wydrukowane
  const [handOver, setHandOver] = useState<{ note: string } | null>(null);
  const markHandedOver = useMutation({
    mutationFn: () => api<{ marked: number }>(`/service-tickets/${id}/protocols-handed-over`, { method: 'POST', body: handOver }),
    onSuccess: (r) => (notifyOk(r.marked === 1 ? 'Protokół oznaczony jako doręczony' : `Oznaczono protokoły jako doręczone: ${r.marked}`), setHandOver(null), refresh()),
    onError: notifyError,
  });
  const saveClaim = useMutation({ mutationFn: () => api(`/service-tickets/${id}/claim`, { method: 'PUT', body: { ...claim, claimDueOn: claim?.claimState === 'zgloszone' ? (claim.claimDueOn ?? null) : null } }), onSuccess: () => (notifyOk('Zapisano reklamację'), setClaim(null), refresh()), onError: notifyError });
  // S08: ponowna wysyłka protokołu ze zgłoszenia (ta sama trasa co w sprawie / zadaniu)
  const [resend, setResend] = useState<{ p: FieldProtocol; email: string; reason: string } | null>(null);
  const sendAgain = useMutation({
    mutationFn: () => api(`/protocols/${resend!.p.id}/resend`, { method: 'POST', body: { email: resend!.email, reason: resend!.reason } }),
    onSuccess: () => (notifyOk('Wiadomość dodana do kolejki wysyłki'), setResend(null), refresh()),
    onError: notifyError,
  });
  // QA qa10302046 P20: dosłanie niedoręczonych protokołów na adres e-mail klienta
  const sendUndelivered = useMutation({
    mutationFn: () => api<{ sent: number; email: string }>(`/service-tickets/${id}/send-protocols`, { method: 'POST' }),
    onSuccess: (r) => (notifyOk(`${r.sent === 1 ? 'Protokół dodany' : 'Protokoły dodane'} do kolejki wysyłki na ${r.email}`), refresh()),
    onError: notifyError,
  });
  const refresh = () => {
    for (const k of ['service-ticket', 'service-tickets', 'service-plans', 'planning-queue', 'planning-board', 'task', 'service-ticket-history', 'service-counters']) void qc.invalidateQueries({ queryKey: [k] });
  };
  const save = useMutation({
    mutationFn: () => {
      const { billing, billingReason, ...rest } = edit!;
      return api(`/service-tickets/${id}`, { method: 'PUT', body: { ...rest, ...(billing ? { billing, billingReason, warranty: billing === 'gwarancja' } : {}), claimDueOn: edit?.claimState === 'zgloszone' ? (edit.claimDueOn ?? null) : null } });
    },
    onSuccess: () => (notifyOk('Zapisano'), setEdit(null), refresh()), onError: notifyError });
  const status = useMutation({
    mutationFn: (body: unknown) => api(`/service-tickets/${id}/status`, { method: 'POST', body }),
    onSuccess: () => (notifyOk('Zmieniono stan zgłoszenia'), setAction(null), refresh()),
    onError: notifyError,
  });
  // „Zaplanuj wizytę” w jednym kroku: zadanie ze zgłoszenia + okno terminu
  const schedule = useMutation({
    mutationFn: async () => {
      const r = await api<{ taskId: string }>(`/service-tickets/${id}/task`, { method: 'POST', body: { taskType: d!.type === 'przeglad' ? 'przeglad' : 'serwis' } });
      return api<TaskDto>(`/tasks/${r.taskId}`);
    },
    onSuccess: (task) => (refresh(), setPlanTask(task)),
    onError: notifyError,
  });
  const visitWorkers = new Set(visitTask.data?.assignments.map((a) => a.userId) ?? []).size;
  const editable = can(me, 'serwis.tworzenie', 'W') && d && d.status !== 'zamkniete' && d.status !== 'anulowane';
  const canPlan = can(me, 'planowanie.edycja', 'W');
  // QA6 P29: pola reklamacji w oknie zamknięcia — zgłoszenie gwarancyjne, wymieniona część albo już zgłoszona reklamacja
  const claimFields = !!d && (d.warranty || d.partsReplaced || d.claimState !== 'brak');
  // QA qa10302243 P5: termin decyzji producenta (przypomnienie) — dotychczasowy albo domyślnie za CLAIM_REMINDER_DAYS dni
  const claimDue = d?.claimDueOn ?? addDays(todayIso(), CLAIM_REMINDER_DAYS);

  const primary = (() => {
    if (!d || !editable) return null;
    if (visit && visitTask.data && canPlan && ['do_zaplanowania', 'zaplanowane'].includes(visit.status))
      return (
        <Button leftSection={<IconCalendarEvent size={16} />} onClick={() => setPlanTask(visitTask.data!)}>
          {visit.status === 'do_zaplanowania' ? 'Zaplanuj wizytę' : 'Zmień termin'}
        </Button>
      );
    if (!visit && d.status !== 'wykonane' && canPlan)
      return (
        <Button leftSection={<IconCalendarEvent size={16} />} loading={schedule.isPending} onClick={() => schedule.mutate()}>
          Zaplanuj wizytę
        </Button>
      );
    if (d.status === 'wykonane')
      return (
        <Button onClick={() => setAction({ action: 'zamknij', note: '', date: todayIso(), claimNumber: d.claimNumber, claimState: d.claimState, claimDueOn: claimDue, billing: d.billing ?? null, billingReason: '' })}>
          Zamknij zgłoszenie
        </Button>
      );
    return null;
  })();

  return (
    <QueryState isLoading={t.isLoading} error={t.error}>
      {d && (
        <Stack gap="md">
          <div>
            <Group justify="space-between" align="flex-start">
              <div>
                <Text fw={700} size="lg">
                  {TICKET_TYPES[d.type]} · {d.clientName ?? '—'}
                </Text>
                <Text size="sm" c="dimmed">
                  {d.number} · {d.siteAddress}
                </Text>
              </div>
              <Group gap="xs">
                {primary}
                {editable && (
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <Button size="xs" variant="default" px={8} aria-label="Więcej">
                        <IconDots size={14} />
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {d.status !== 'wykonane' && <Menu.Item onClick={() => setEdit({ priority: d.priority, description: d.description, warranty: d.warranty, billing: d.billing ?? (d.warranty ? 'gwarancja' : null), billingReason: '', companyId: d.companyId, claimNumber: d.claimNumber, claimState: d.claimState, claimDueOn: claimDue, deviceIds: d.devices.map((x) => x.id) })}>Edytuj zgłoszenie</Menu.Item>}
                      {d.status === 'nowe' && <Menu.Item onClick={() => status.mutate({ action: 'do_umowienia' })}>Oznacz „do umówienia”</Menu.Item>}
                      {d.status !== 'wykonane' && !visit && <Menu.Item onClick={() => setAction({ action: 'wykonane', note: '', date: todayIso() })}>Wykonane bez wizyty w aplikacji…</Menu.Item>}
                      <Menu.Divider />
                      <Menu.Item color="red" onClick={() => setAction({ action: 'anuluj', note: '', date: todayIso() })}>
                        Anuluj zgłoszenie
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>
            </Group>
            <div style={{ overflowX: 'auto', marginTop: 10 }}>
              <Steps status={d.status} />
            </div>
          </div>

          {d.overdue && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              Przegląd zaległy (termin i tolerancja minęły) — termin był {dateOnly(d.dueDate)}. Umów wizytę jak najszybciej{d.warranty ? ' — ryzyko utraty gwarancji' : ''}.
            </Alert>
          )}
          {d.lateInspections.length > 0 && <LateInspectionsAlert items={d.lateInspections} warranty={d.warranty} />}
          {/* QA qa10301331 P19: zaległy przegląd tej samej instalacji — do umówienia przy tej wizycie */}
          {(d.overduePlans ?? []).map((p) => (
            <Alert key={p.planId} color="yellow" icon={<IconAlertTriangle size={16} />}>
              Instalacja ma zaległy przegląd{' '}
              {p.ticketId ? (
                <Anchor component={Link} to={`/serwis?zgloszenie=${p.ticketId}`} size="sm">
                  {p.ticketNumber}
                </Anchor>
              ) : (
                <Anchor component={Link} to={`/instalacje?id=${d.installationId}`} size="sm">
                  {p.deviceName ?? 'instalacji'}
                </Anchor>
              )}
              {p.ticketId && p.deviceName ? ` (${p.deviceName})` : ''} (termin {dateOnly(p.due)}) — umów razem z tą wizytą.
            </Alert>
          ))}

          <Paper withBorder p="sm" radius="md">
            <Text size="sm" fw={600} mb={6}>
              Wizyta
            </Text>
            {visit ? (
              <Group justify="space-between">
                <div>
                  <Text size="sm" fw={600}>
                    {visitTask.data ? termText(visitTask.data) : '…'} · {TASK_STATUSES[visit.status as TaskStatus]}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {visit.number}
                    {visitTask.data?.assignments.length ? ` · ${visitWorkers} ${plural(visitWorkers, 'pracownik', 'pracowników', 'pracowników')}` : ''}
                  </Text>
                </div>
                <Anchor component={Link} to={`/planowanie?zadanie=${visit.id}`} size="sm">
                  Otwórz zadanie
                </Anchor>
              </Group>
            ) : d.status === 'wykonane' || d.status === 'zamkniete' ? (
              <Text size="sm" c="dimmed">
                Wykonane{d.tasks.length ? '' : ' bez wizyty w aplikacji'}.
              </Text>
            ) : (
              <Text size="sm" c="dimmed">
                Brak terminu{d.dueDate ? ` — przegląd należy wykonać do ${dateOnly(d.dueDate)}` : ''}.
              </Text>
            )}
            {d.tasks.filter((x) => x.id !== visit?.id).length > 0 && (
              <Text size="xs" c="dimmed" mt={4}>
                Wcześniejsze: {d.tasks.filter((x) => x.id !== visit?.id).map((x) => `${x.number} (${TASK_STATUSES[x.status as TaskStatus].toLowerCase()})`).join(', ')}
              </Text>
            )}
          </Paper>

          {!edit ? (
            <Table fz="sm" verticalSpacing={4} withRowBorders={false}>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td w={150} c="dimmed" style={{ verticalAlign: 'top' }}>
                    Opis
                  </Table.Td>
                  <Table.Td style={{ whiteSpace: 'pre-line' }}>{d.description}</Table.Td>
                </Table.Tr>
                {d.devices.length > 0 && (
                  <Table.Tr>
                    <Table.Td c="dimmed">Urządzenia</Table.Td>
                    <Table.Td>{d.devices.map((x) => `${x.name}${x.serialNumber ? ` (SN ${x.serialNumber})` : ''}`).join(', ')}</Table.Td>
                  </Table.Tr>
                )}
                {(!!d.parts?.length || !!d.protocolParts?.length) && (
                  <Table.Tr>
                    <Table.Td c="dimmed" style={{ verticalAlign: 'top' }}>
                      Części i materiały
                    </Table.Td>
                    <Table.Td>
                      {d.protocolParts?.map((p, i) => (
                        <Text key={`p${i}`} size="sm" style={{ whiteSpace: 'pre-line' }}>
                          {p.text}
                          <Text span size="xs" c="dimmed">
                            {' '}
                            · wymienione części z protokołu {p.protocolCode}
                            {d.tasks.length > 1 ? ` (${p.taskNumber})` : ''}
                          </Text>
                        </Text>
                      ))}
                      {(d.parts ?? []).map((p, i) => (
                        <Text key={i} size="sm">
                          {p.name} — {p.quantity.replace('.', ',')} {p.unit}
                          {d.tasks.length > 1 && (
                            <Text span size="xs" c="dimmed">
                              {' '}
                              · {p.taskNumber}
                            </Text>
                          )}
                        </Text>
                      ))}
                      <Text size="xs" c="dimmed">
                        Rozliczenie wg zgłoszenia: {d.billing ? TICKET_BILLING[d.billing].toLowerCase() : 'nie wybrano'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {d.installationId && (
                  <Table.Tr>
                    <Table.Td c="dimmed">Instalacja</Table.Td>
                    <Table.Td>
                      <Anchor component={Link} to={`/instalacje?id=${d.installationId}`} size="sm">
                        Karta instalacji
                      </Anchor>
                    </Table.Td>
                  </Table.Tr>
                )}
                <Table.Tr>
                  <Table.Td c="dimmed">Priorytet</Table.Td>
                  <Table.Td>{TICKET_PRIORITIES[d.priority]}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td c="dimmed">Rozliczenie</Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      {d.billing ? TICKET_BILLING[d.billing] : 'Nie wybrano (gwarancja / płatna / bez opłaty)'}
                      {d.companyId && <CompanyBadgeById id={d.companyId} />}
                      {d.billingState && (
                        <Badge size="sm" color={d.billingState === 'zafakturowane' ? 'green' : 'orange'}>
                          {TICKET_BILLING_STATES[d.billingState]}
                        </Badge>
                      )}
                      {/* QA qa10311123 3.3: zmiana rozliczenia po zamknięciu (z powodem) */}
                      {(d.status === 'zamkniete' || d.status === 'wykonane') && can(me, 'serwis.tworzenie', 'W') && (
                        <Anchor component="button" size="xs" onClick={() => setBillingEdit({ billing: d.billing ?? 'platna', reason: '' })}>
                          Zmień
                        </Anchor>
                      )}
                      {d.billingState === 'do_faktury' && (can(me, 'serwis.tworzenie', 'W') || can(me, 'faktury.wystawianie', 'W')) && (
                        <Anchor component="button" size="xs" onClick={() => setInvoice({ invoiceNumber: '' })}>
                          Wpisz numer faktury
                        </Anchor>
                      )}
                    </Group>
                    {d.billingState === 'zafakturowane' && d.billingInvoice && (
                      <Text size="xs" c="dimmed">
                        Faktura {d.billingInvoice.number} · {formatDateTime(d.billingInvoice.at)}
                        {d.billingInvoice.by ? ` · ${d.billingInvoice.by}` : ''}
                      </Text>
                    )}
                    {d.billingState === 'do_faktury' && (
                      <Text size="xs" c="dimmed">
                        Bez wpłaty online księgowość dostaje powiadomienie „Wystaw fakturę za serwis {d.number}”; po wpłacie online fakturę wystawia CRM.
                      </Text>
                    )}
                    {d.billing === 'platna' && d.status !== 'zamkniete' && !d.billingInvoice && (
                      <Text size="xs" c="dimmed">
                        Po zamknięciu zgłoszenia księgowość dostanie powiadomienie o fakturze.
                      </Text>
                    )}
                    {d.billing && d.billing !== 'gwarancja' && d.billingReason && (
                      <Text size="xs" c="dimmed">
                        Powód: {d.billingReason}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
                {/* docs/26: pozycje do zapłaty, płatność online, faktura po wpłacie */}
                {(can(me, 'serwis.platnosci') || can(me, 'faktury.podglad')) && (
                  <Table.Tr>
                    <Table.Td c="dimmed">Do zapłaty</Table.Td>
                    <Table.Td>
                      <ServiceChargeBlock ticketId={d.id} me={me} warranty={d.warranty} />
                    </Table.Td>
                  </Table.Tr>
                )}
                {(d.warranty || d.claimState !== 'brak') && (
                  <Table.Tr>
                    <Table.Td c="dimmed">U producenta</Table.Td>
                    <Table.Td>
                      {CLAIM_STATES[d.claimState]}
                      {d.claimNumber ? ` (${d.claimNumber})` : ''}
                      {/* QA6 P29: reklamacja pilnowana także po zamknięciu zgłoszenia */}
                      {(d.status === 'zamkniete' || d.status === 'wykonane') && can(me, 'serwis.tworzenie', 'W') && (
                        <Anchor component="button" size="xs" ml={8} onClick={() => setClaim({ claimNumber: d.claimNumber, claimState: d.claimState, claimDueOn: claimDue })}>
                          Zmień
                        </Anchor>
                      )}
                      {/* QA qa10302243 P5: przypomnienie o decyzji producenta */}
                      {d.claimDueOn && (
                        <Text size="xs" c={d.claimOverdue ? 'red.7' : 'dimmed'}>
                          Decyzja producenta do {dateOnly(d.claimDueOn)}
                          {d.claimOverdue ? ' — termin minął, sprawdź u producenta' : ''}
                          {d.claimOwnerName ? ` · przypomnienie: ${d.claimOwnerName}` : ''}
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                )}
                <Table.Tr>
                  <Table.Td c="dimmed">Zgłaszający</Table.Td>
                  <Table.Td>
                    {d.reporter || '—'} · {formatDateTime(d.createdAt)}
                  </Table.Td>
                </Table.Tr>
                {d.closeReason && (
                  <Table.Tr>
                    <Table.Td c="dimmed">Uwagi zamknięcia</Table.Td>
                    <Table.Td>{d.closeReason}</Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          ) : (
            <Paper withBorder p="sm" radius="md">
              <Stack gap="xs">
                <Textarea label="Opis" autosize value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.currentTarget.value })} />
                <Group grow>
                  <Select label="Priorytet" data={Object.entries(TICKET_PRIORITIES).map(([value, label]) => ({ value, label }))} value={edit.priority} onChange={(v) => v && setEdit({ ...edit, priority: v })} />
                  <Select label="Firma rozliczająca" clearable data={(companies.data ?? []).map((c) => ({ value: c.id, label: c.shortName }))} value={edit.companyId} onChange={(v) => setEdit({ ...edit, companyId: v })} />
                </Group>
                {d.installationId && (
                  <MultiSelect
                    label="Urządzenia"
                    description="Dopisz kolejne urządzenie instalacji, którego dotyczy usterka"
                    data={(inst.data?.devices ?? [])
                      .filter((x) => x.status === 'czynne' || edit.deviceIds.includes(x.id))
                      .map((x) => ({ value: x.id, label: [`${x.name}${x.serialNumber ? ` (SN ${x.serialNumber})` : ''}`, warrantyText(x.warrantyUntil, todayIso())].filter(Boolean).join(' · ') }))}
                    value={edit.deviceIds}
                    onChange={(v) => setEdit({ ...edit, deviceIds: v })}
                  />
                )}
                <BillingFields
                  billing={edit.billing}
                  reason={edit.billingReason}
                  needReason={edit.billing !== (d.billing ?? null) && billingReasonRequired(edit.billing, d.warranty)}
                  onChange={(billing, billingReason) => setEdit({ ...edit, billing, billingReason, warranty: billing ? billing === 'gwarancja' : edit.warranty })}
                />
                <Group grow>
                  <ClaimNumber state={edit.claimState} value={edit.claimNumber} onChange={(v) => setEdit({ ...edit, claimNumber: v })} />
                  <Select label="Stan u producenta" data={Object.entries(CLAIM_STATES).map(([value, label]) => ({ value, label }))} value={edit.claimState} onChange={(v) => v && setEdit({ ...edit, claimState: v })} />
                </Group>
                {edit.claimState === 'zgloszone' && <ClaimDue value={edit.claimDueOn ?? null} onChange={(v) => setEdit({ ...edit, claimDueOn: v })} />}
                <Group justify="flex-end">
                  <Button variant="default" size="xs" onClick={() => setEdit(null)}>
                    Anuluj
                  </Button>
                  <Button
                    size="xs"
                    loading={save.isPending}
                    disabled={claimNumberMissing(edit.claimState, edit.claimNumber) || (edit.billing !== (d.billing ?? null) && billingReasonRequired(edit.billing, d.warranty) && edit.billingReason.trim().length < 3)}
                    onClick={() => save.mutate()}
                  >
                    Zapisz
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {d.protocols.length > 0 && (
            <Paper withBorder p="sm" radius="md">
              <Text size="sm" fw={600} mb={6}>
                Protokoły
              </Text>
              {d.protocols.map((p) => (
                <Group key={p.id} gap={6}>
                  <Text size="sm" td={p.status === 'uniewazniony' ? 'line-through' : undefined}>
                    {p.code} — {PROTOCOL_KINDS[p.kind]}, {formatDateTime(p.signedAt)}
                  </Text>
                  <Badge size="xs" color={p.handedOver ? 'green' : deliveryColor(p.delivery)}>
                    {p.handedOver && p.delivery === 'bez_wysylki' ? 'Doręczono poza e-mailem' : PROTOCOL_DELIVERY_STATES[p.delivery]}
                  </Badge>
                  <Anchor href={`/api/protocols/${p.id}/pdf`} target="_blank" size="xs">
                    PDF
                  </Anchor>
                  {can(me, 'protokoly.wysylka') && p.status !== 'uniewazniony' && !PDF_PENDING.includes(p.delivery) && (
                    <Anchor component="button" size="xs" onClick={() => setResend({ p, email: p.email ?? '', reason: '' })}>
                      Wyślij ponownie
                    </Anchor>
                  )}
                </Group>
              ))}
              {(d.undeliveredProtocolIds?.length ?? 0) > 0 && (
                <Alert color="orange" icon={<IconAlertTriangle size={16} />} mt="xs" p="xs">
                  <Group justify="space-between" gap="xs">
                    <Text size="sm">
                      {d.clientEmail
                        ? `Niewysłane klientowi: ${d.undeliveredProtocolIds!.length} — klient ma już adres ${d.clientEmail}.`
                        : `Niewysłane klientowi: ${d.undeliveredProtocolIds!.length} — klient nie ma adresu e-mail. Dopisz go w karcie klienta albo oznacz protokół jako doręczony na miejscu / wydrukiem.`}
                    </Text>
                    <Group gap="xs">
                      {d.clientEmail && can(me, 'protokoly.wysylka') && (
                        <Button size="xs" loading={sendUndelivered.isPending} onClick={() => sendUndelivered.mutate()}>
                          Wyślij niedoręczone protokoły ({d.undeliveredProtocolIds!.length})
                        </Button>
                      )}
                      {can(me, 'serwis.tworzenie', 'W') && (
                        <Button size="xs" variant="default" onClick={() => setHandOver({ note: '' })}>
                          Doręczono poza e-mailem…
                        </Button>
                      )}
                    </Group>
                  </Group>
                </Alert>
              )}
            </Paper>
          )}

          <TicketHistory id={id} />

          {planTask && (
            <PlanDialog
              me={me}
              task={planTask}
              onClose={() => {
                setPlanTask(null);
                refresh();
              }}
            />
          )}
          <Modal opened={!!action} onClose={() => setAction(null)} title={action?.action === 'wykonane' ? 'Wykonane bez wizyty w aplikacji' : action?.action === 'zamknij' ? 'Zamknięcie zgłoszenia' : 'Anulowanie zgłoszenia'}>
            {action && (
              <Stack>
                {action.action === 'wykonane' && (
                  <>
                    <Text size="sm">Np. przegląd wykonany przez serwis zewnętrzny. Przegląd z planu przesunie plan na kolejny termin (wg trybu planu).</Text>
                    <TextInput type="date" label="Data wykonania" value={action.date} onChange={(e) => setAction({ ...action, date: e.currentTarget.value })} />
                  </>
                )}
                {action.action === 'zamknij' && d.warranty && d.partsReplaced && d.claimState === 'brak' && (
                  <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Zgłoś reklamację u producenta">
                    Naprawa gwarancyjna z wymienioną częścią — zgłoś ją producentowi, a numer i stan wpisz poniżej. Otwarta reklamacja zostaje na liście „Reklamacje u producenta” do rozliczenia.
                  </Alert>
                )}
                {action.action === 'zamknij' && claimFields && d.lateInspections.length > 0 && <LateInspectionsAlert items={d.lateInspections} warranty={d.warranty} />}
                {action.action === 'zamknij' && claimFields && (
                  <Group grow>
                    <ClaimNumber state={action.claimState ?? 'brak'} value={action.claimNumber ?? ''} onChange={(v) => setAction({ ...action, claimNumber: v })} />
                    <Select label="Stan u producenta" data={Object.entries(CLAIM_STATES).map(([value, label]) => ({ value, label }))} value={action.claimState ?? 'brak'} onChange={(v) => v && setAction({ ...action, claimState: v })} />
                  </Group>
                )}
                {action.action === 'zamknij' && claimFields && action.claimState === 'zgloszone' && <ClaimDue value={action.claimDueOn ?? null} onChange={(v) => setAction({ ...action, claimDueOn: v })} />}
                {/* QA qa10311123 3.3: jawna decyzja o rozliczeniu naprawy przy zamknięciu */}
                {action.action === 'zamknij' && (
                  <BillingFields
                    billing={action.billing ?? null}
                    reason={action.billingReason ?? ''}
                    needReason={action.billing !== (d.billing ?? null) && billingReasonRequired(action.billing, d.warranty)}
                    onChange={(billing, billingReason) => setAction({ ...action, billing, billingReason })}
                    required
                    hint={action.billing === 'platna' ? `Po zamknięciu księgowość dostanie powiadomienie „Wystaw fakturę za serwis ${d.number}”.` : undefined}
                  />
                )}
                <TextInput label={action.action === 'zamknij' ? 'Uwagi (opcjonalnie)' : 'Powód / opis'} required={action.action !== 'zamknij'} description={action.action !== 'zamknij' ? minHint(action.note) : undefined} value={action.note} onChange={(e) => setAction({ ...action, note: e.currentTarget.value })} />
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setAction(null)}>
                    {/* „Anuluj zgłoszenie” to główna akcja — drugi przycisk „Wróć” */}
                    {action.action === 'anuluj' ? 'Wróć' : 'Anuluj'}
                  </Button>
                  <Button
                    color={action.action === 'anuluj' ? 'red' : undefined}
                    loading={status.isPending}
                    disabled={
                      action.action !== 'zamknij'
                        ? action.note.trim().length < 3
                        : (claimFields && claimNumberMissing(action.claimState, action.claimNumber)) ||
                          !action.billing ||
                          (action.billing !== (d.billing ?? null) && billingReasonRequired(action.billing, d.warranty) && (action.billingReason ?? '').trim().length < 3)
                    }
                    onClick={() => {
                      const billing = action.billing ? { billing: action.billing, billingReason: action.billingReason ?? '' } : {};
                      status.mutate(
                        action.action === 'wykonane'
                          ? { action: 'wykonane', date: action.date, note: action.note }
                          : action.action === 'zamknij' && claimFields
                            ? { action: 'zamknij', note: action.note, claimNumber: action.claimNumber ?? '', claimState: action.claimState ?? 'brak', ...(action.claimState === 'zgloszone' && action.claimDueOn ? { claimDueOn: action.claimDueOn } : {}), ...billing }
                            : action.action === 'zamknij'
                              ? { action: 'zamknij', note: action.note, ...billing }
                              : { action: action.action, note: action.note },
                      );
                    }}
                  >
                    {action.action === 'anuluj' ? 'Anuluj zgłoszenie' : action.action === 'zamknij' ? 'Zamknij zgłoszenie' : 'Oznacz jako wykonane'}
                  </Button>
                </Group>
              </Stack>
            )}
          </Modal>
          <Modal opened={!!billingEdit} onClose={() => setBillingEdit(null)} title="Rozliczenie naprawy">
            {billingEdit && (
              <Stack>
                <BillingFields billing={billingEdit.billing} reason={billingEdit.reason} needReason onChange={(b, reason) => b && setBillingEdit({ billing: b, reason })} required hint={d.status === 'zamkniete' ? 'Zmiana po zamknięciu zgłoszenia — powód zapisze się w historii.' : 'Zgłoszenie jest wykonane — powód zapisze się w historii.'} />
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setBillingEdit(null)}>
                    Anuluj
                  </Button>
                  <Button loading={saveBilling.isPending} disabled={billingEdit.reason.trim().length < 3} onClick={() => saveBilling.mutate()}>
                    Zapisz
                  </Button>
                </Group>
              </Stack>
            )}
          </Modal>
          <Modal opened={!!invoice} onClose={() => setInvoice(null)} title={`Faktura za serwis ${d.number}`}>
            {invoice && (
              <Stack>
                <Text size="sm">Wpisz numer wystawionej faktury. Powiadomienie księgowości zniknie, a numer trafi do historii zgłoszenia.</Text>
                <TextInput label="Numer faktury" required data-autofocus value={invoice.invoiceNumber} onChange={(e) => setInvoice({ invoiceNumber: e.currentTarget.value })} />
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setInvoice(null)}>
                    Anuluj
                  </Button>
                  <Button loading={saveInvoice.isPending} disabled={!invoice.invoiceNumber.trim()} onClick={() => saveInvoice.mutate()}>
                    Zapisz
                  </Button>
                </Group>
              </Stack>
            )}
          </Modal>
          <Modal opened={!!claim} onClose={() => setClaim(null)} title="Reklamacja u producenta">
            {claim && (
              <Stack>
                {d.lateInspections.length > 0 && <LateInspectionsAlert items={d.lateInspections} warranty={d.warranty} />}
                <ClaimNumber state={claim.claimState} value={claim.claimNumber} onChange={(v) => setClaim({ ...claim, claimNumber: v })} />
                <Select label="Stan u producenta" data={Object.entries(CLAIM_STATES).map(([value, label]) => ({ value, label }))} value={claim.claimState} onChange={(v) => v && setClaim({ ...claim, claimState: v })} />
                {claim.claimState === 'zgloszone' && <ClaimDue value={claim.claimDueOn ?? null} onChange={(v) => setClaim({ ...claim, claimDueOn: v })} />}
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setClaim(null)}>
                    Anuluj
                  </Button>
                  <Button loading={saveClaim.isPending} disabled={claimNumberMissing(claim.claimState, claim.claimNumber)} onClick={() => saveClaim.mutate()}>
                    Zapisz
                  </Button>
                </Group>
              </Stack>
            )}
          </Modal>
          <Modal opened={!!handOver} onClose={() => setHandOver(null)} title="Doręczono poza e-mailem">
            {handOver && (
              <Stack>
                <Text size="sm">
                  {d.undeliveredProtocolIds?.length === 1 ? 'Protokół zejdzie' : `Protokoły (${d.undeliveredProtocolIds?.length ?? 0}) zejdą`} z listy „Protokoły niedoręczone”. Wpis trafi do historii zgłoszenia.
                </Text>
                <TextInput label="Jak doręczono (opcjonalnie)" placeholder="np. wręczony na miejscu, wydruk wysłany pocztą" value={handOver.note} onChange={(e) => setHandOver({ note: e.currentTarget.value })} />
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setHandOver(null)}>
                    Anuluj
                  </Button>
                  <Button loading={markHandedOver.isPending} onClick={() => markHandedOver.mutate()}>
                    Oznacz jako doręczone
                  </Button>
                </Group>
              </Stack>
            )}
          </Modal>
          <Modal opened={!!resend} onClose={() => setResend(null)} title={`Ponowna wysyłka ${resend?.p.code ?? ''}`}>
            {resend && (
              <Stack>
                <TextInput label="Adres e-mail" required value={resend.email} onChange={(e) => setResend({ ...resend, email: e.currentTarget.value })} />
                <TextInput label="Powód" required description={resend.reason.trim().length < 3 ? minHint(resend.reason, 3) : 'Zapisze się w historii'} value={resend.reason} onChange={(e) => setResend({ ...resend, reason: e.currentTarget.value })} />
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setResend(null)}>
                    Anuluj
                  </Button>
                  <Button loading={sendAgain.isPending} disabled={!resend.email.trim() || resend.reason.trim().length < 3} onClick={() => sendAgain.mutate()}>
                    Wyślij
                  </Button>
                </Group>
              </Stack>
            )}
          </Modal>
        </Stack>
      )}
    </QueryState>
  );
}

/** QA qa10302046 P18: historia zgłoszenia — kto założył, umówił wizytę, wysłał protokół, zamknął (dziennik operacji). */
function TicketHistory({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ['service-ticket-history', id], queryFn: () => api<Page<AuditEntryDto>>(`/service-tickets/${id}/history?limit=200`), enabled: open });
  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between">
        <Text size="sm" fw={600}>
          Historia
        </Text>
        <Anchor component="button" size="xs" onClick={() => setOpen(!open)}>
          {open ? 'Zwiń' : 'Pokaż'}
        </Anchor>
      </Group>
      {open && (
        <QueryState isLoading={q.isLoading} error={q.error}>
          <Stack gap={4} mt={6}>
            {q.data?.items.length === 0 && (
              <Text size="sm" c="dimmed">
                Brak wpisów.
              </Text>
            )}
            {q.data?.items.map((e) => (
              <Group key={e.id} gap="sm" wrap="wrap" align="flex-start" py={4} style={{ borderBottom: '1px solid var(--app-line)' }}>
                <Text size="xs" c="dimmed" w={110} style={{ flexShrink: 0 }}>
                  {formatDateTime(e.at)}
                </Text>
                <Text size="xs" w={120} style={{ flexShrink: 0 }}>
                  {e.actorName ?? 'system'}
                </Text>
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <Text size="sm">{ACTION_LABELS[e.action] ?? e.action}</Text>
                  {e.reason && (
                    <Text size="xs" c="dimmed">
                      Powód: {e.reason}
                    </Text>
                  )}
                  {e.changes != null && <Changes value={e.changes} />}
                </div>
              </Group>
            ))}
          </Stack>
        </QueryState>
      )}
    </Paper>
  );
}

/** QA qa10311123 3.3: rozliczenie naprawy — gwarancja / płatna / bez opłaty, z powodem, gdy wymagany. */
export function BillingFields({ billing, reason, needReason, onChange, required, hint }: { billing: TicketBilling | null; reason: string; needReason: boolean; onChange: (b: TicketBilling | null, reason: string) => void; required?: boolean; hint?: string }) {
  return (
    <>
      <Select
        label="Rozliczenie naprawy"
        required={required}
        placeholder="Wybierz"
        data={Object.entries(TICKET_BILLING).map(([value, label]) => ({ value, label }))}
        value={billing}
        onChange={(v) => onChange((v as TicketBilling | null) ?? null, reason)}
        description={hint}
      />
      {needReason && (
        <TextInput label="Powód rozliczenia" required description={reason.trim().length < 3 ? BILLING_REASON_REQUIRED : 'Zapisze się w historii'} placeholder="np. przegląd po tolerancji — gwarancja nie obejmuje" value={reason} onChange={(e) => onChange(billing, e.currentTarget.value)} />
      )}
    </>
  );
}

/** QA qa10302243 P5: termin decyzji producenta — przypomnienie dla osoby, która zgłasza reklamację. */
/** QA 10310111 3.8: numer reklamacji — wymagany przy decyzji producenta, przy „zgłoszone” tylko ostrzeżenie. */
function ClaimNumber({ state, value, onChange }: { state: string; value: string; onChange: (v: string) => void }) {
  const missing = !value.trim();
  return (
    <TextInput
      label="Nr zgłoszenia u producenta"
      required={claimNumberMissing(state, '')}
      error={claimNumberMissing(state, value) ? CLAIM_NUMBER_REQUIRED : undefined}
      description={state === 'zgloszone' && missing ? 'Bez numeru trudno powiązać decyzję producenta — dopisz go, gdy przyjdzie' : undefined}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
}

function ClaimDue({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  return (
    <TextInput
      type="date"
      label="Termin decyzji producenta"
      description={`Przypomnienie dla Ciebie w tym dniu (domyślnie ${CLAIM_REMINDER_DAYS} dni). Zniknie po wpisaniu decyzji producenta.`}
      min={todayIso()}
      value={value ?? ''}
      onChange={(e) => onChange(e.currentTarget.value || null)}
    />
  );
}

/** QA7 P12: przegląd instalacji wykonany po tolerancji (12 miesięcy) — przy naprawie gwarancyjnej i reklamacji u producenta. */
function LateInspectionsAlert({ items, warranty }: { items: ServiceTicketDto['lateInspections']; warranty: boolean }) {
  return (
    <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Spóźniony przegląd instalacji">
      <Stack gap={2}>
        {items.map((l) => (
          <Text key={`${l.due}-${l.doneOn}`} size="sm">
            {lateInspectionText(l)}
          </Text>
        ))}
        <Text size="xs" c="dimmed">
          {warranty ? 'Zgłoszenie oznaczone jako gwarancyjne — sprawdź warunki gwarancji producenta przed rozliczeniem naprawy i reklamacją.' : 'Gwarancja wymagająca przeglądów może być zagrożona.'}
        </Text>
      </Stack>
    </Alert>
  );
}
